import {Utils} from './utils';
import {Constants} from '../constants';
import includes from 'lodash-es/includes';

export class SegmentHandler {
    /**
     * List of allowed segments
     * @var {Array<object>}
     */
    private _allowedSegments: Array<string> = [];

    /**
     * List of segments passed on query param
     * @var {Array<string>}
     */
    private _segments: Array<string> = [];

    /**
     * Selected segment
     * @var {string}
     */
    private _selectedSegment: string = '';

    /**
     * Default segment in app configuration
     * @var {string}
     */
    private _appDefaultSegment: string = '';

    /**
     * Initialize variables
     *
     * @returns {void}
     */
    public initialize(allowedSegments: Array<string>, defaultSegment: string): SegmentHandler {
        this._allowedSegments = allowedSegments;

        this._appDefaultSegment = defaultSegment;

        this._setSegmentsFromQueryParams();
        this._setDefaultSelectedSegment();

        return this;
    }

    /**
     * Retrieves the segments based on the query param and allowed segments
     *
     * @param allowedSegments
     * @returns {Array<string>}
     */
    public getSegments(): Array<string> {
        return this._segments;
    }

    /**
     * Returns if the segments set by the guest are allowed or not.
     *
     * @returns {boolean}
     */
    public isValidSegments(): boolean {
        return this._segments.length > 0;
    }

    /**
     * Retrieves the default selected segment
     *
     * @returns {string}
     */
    public getSelectedSegment(): string {
        return this._selectedSegment;
    }

    /**
     * Gets the default selected segment from the "defaultSegment" query param
     *
     * @returns {void}
     */
    private _setDefaultSelectedSegment(): void {
        let defaultSegment = Utils.getValueFromQueryParam(Constants.PARAM_DEFAULT_SEGMENT);

        // If defaultSegment is not on the query param, we default it to the first one on that list
        // If segments query param is empty, default to "tickets"
        if (Object.keys(this._segments).length === 0) {
            defaultSegment = includes(this._allowedSegments, defaultSegment)
                ? defaultSegment
                : this._appDefaultSegment;
        } else {
            if (!includes(this._segments, defaultSegment)) {
                defaultSegment = (!!this._segments[0]) ? this._segments[0] : this._appDefaultSegment;
            }
        }

        this._selectedSegment = defaultSegment;
    }

    /**
     * Gets the segments from the "segments" query param and sets the current segments if they are valid
     *
     * @private
     * @returns {void}
     */
    private _setSegmentsFromQueryParams(): void {
        const segments = Utils.getValueFromQueryParam(Constants.PARAM_SEGMENTS).split(',');
        this._segments = segments.filter((value, index) => {
            return includes(this._allowedSegments, value) && segments.indexOf(value) === index;
        });
    }

    /**
     * Verifies if the segments set by the guest are valid for standalone calendar
     * @returns {boolean}
     */
    public validStandaloneCalendarSegment(): boolean {
        const urlSegments = Utils.getValueFromQueryParam(Constants.PARAM_SEGMENTS).split(',');
        return urlSegments.length === 1 && includes(this._allowedSegments, urlSegments[0]);
    }
}
